import { getAuth } from 'firebase/auth';
import { app } from './firebase';

const CHECKOUT_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/createCheckoutSession';
const SYNC_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/syncSubscription';
const PORTAL_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/createPortalSession';
const PRICING_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/getPricing';

// Local (not UTC) calendar day — the server trusts this for recurring-tour
// check-in so it matches the organizer's actual day, not the function's clock.
function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Creates a Stripe Checkout session and returns the hosted page URL.
 * plan: 'monthly' | 'yearly'
 */
export async function createCheckoutSession(plan) {
  if (!app) throw new Error('Firebase not configured.');

  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in to subscribe.');

  const idToken = await user.getIdToken();

  const res = await fetch(CHECKOUT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ plan, appUrl: window.location.origin }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.url;
}

/**
 * Live subscription pricing straight from Stripe — no auth required.
 * Returns { monthly: {amount, currency, interval}, yearly: {...} }.
 */
export async function fetchPricing() {
  const res = await fetch(PRICING_URL);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

/**
 * Pulls the current user's subscription from Stripe and writes it to Firestore.
 * Call this after a successful checkout redirect.
 */
export async function syncSubscription() {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in to sync.');

  const idToken = await user.getIdToken();
  const res = await fetch(SYNC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const TOUR_PUBLISH_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/createTourPublishingSession';
const TOUR_TICKET_URL  =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/createTourTicketSession';
const TOUR_SYNC_URL    =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/syncTourStatus';

/**
 * Creates a Stripe Checkout session for the $9.99 tour publishing fee.
 * Returns the hosted Checkout URL to redirect the organizer to.
 */
export async function createTourPublishingSession(tourId) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(TOUR_PUBLISH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId, appUrl: window.location.origin }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.url;
}

/**
 * Creates a Stripe Checkout session for a paid tour ticket.
 * `lessonBooking` (lessons only): { hours, startTime, rentBike } — the server
 * recomputes the price from the tour's own hourlyRate/bikeHourlyRate, never
 * trusting a client-sent total.
 * Returns the hosted Checkout URL.
 */
export async function createTourTicketSession(tourId, tourDate = null, lessonBooking = null) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(TOUR_TICKET_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({
      tourId, tourDate, appUrl: window.location.origin,
      ...(lessonBooking ? {
        hours: lessonBooking.hours,
        startTime: lessonBooking.startTime,
        rentBike: !!lessonBooking.rentBike,
      } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.url;
}

/**
 * Polls Stripe directly to check if a tour publishing payment completed.
 * Call this when the user returns from Checkout with ?tour_published=<id>.
 */
export async function syncTourStatus(tourId) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(TOUR_SYNC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.published === true;
}

const TOUR_CONFIRM_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/confirmTicket';

/**
 * Fallback after a successful ticket Checkout: confirms the session and ensures
 * the registration exists even if the webhook lags. Returns { confirmed }.
 */
export async function confirmTicket(tourId, sessionId) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(TOUR_CONFIRM_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId, sessionId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const CHECKIN_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/checkInRider';

/**
 * Organizer scans a rider's QR to check them in.
 * `code` is the raw QR payload ("MTR1.<tourId>.<uid>.<token>").
 * Returns { status: 'ok' | 'already' | 'invalid', rider?, reason? }.
 */
export async function checkInRider(code) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(CHECKIN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ code, today: todayYMD() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const TRACK_VIEW_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/trackTourView';

/**
 * Fire-and-forget view counter — no auth required (anonymous browsers view
 * tours too), and callers should swallow any failure since a missed view
 * shouldn't ever surface as an error to whoever's just looking at a tour.
 */
export async function trackTourView(tourId) {
  await fetch(TRACK_VIEW_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId, dateKey: todayYMD() }),
  });
}

const TOUR_REFUND_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/refundTour';

/**
 * Owner-only: cancel a tour and refund every confirmed rider.
 * Returns { refunded, released, failed }.
 */
export async function refundTour(tourId) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(TOUR_REFUND_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const CANCEL_REGISTRATION_SELF_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/cancelRegistrationSelf';

/**
 * Rider-only: cancel their own registration. Refunds the ticket price (never
 * the booking fee) automatically, but only if it's still ≥48h before the ride
 * — the server decides eligibility from the tour's own date, not the client.
 * Returns { ok, refunded, refundEligible, refundError? }.
 */
export async function cancelRegistrationSelf(tourId, regId) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(CANCEL_REGISTRATION_SELF_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId, regId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const CANCEL_TOUR_DATE_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/cancelTourDate';

/**
 * Owner-only: cancel a single upcoming occurrence of a recurring tour (not the
 * whole series). Only succeeds when nobody has registered for that date yet —
 * cancel/refund existing registrations first via refundTour otherwise.
 */
export async function cancelTourDate(tourId, date) {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(CANCEL_TOUR_DATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ tourId, date, today: todayYMD() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

const CONNECT_ONBOARDING_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/createConnectOnboarding';
const CONNECT_STATUS_URL =
  'https://us-central1-roamr-4f8ad.cloudfunctions.net/getConnectStatus';

/**
 * Creates (or resumes) Stripe Express onboarding for the current user.
 * Returns the hosted Stripe Connect onboarding URL.
 */
export async function createConnectOnboarding() {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(CONNECT_ONBOARDING_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ returnUrl: window.location.origin }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.url;
}

/**
 * Returns the current user's Stripe Connect account status.
 * { connected, chargesEnabled, dashboardUrl }
 */
export async function getConnectStatus() {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(CONNECT_STATUS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data;
}

/**
 * Creates a Stripe Billing Portal session and returns the URL.
 */
export async function createPortalSession() {
  if (!app) throw new Error('Firebase not configured.');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const idToken = await user.getIdToken();
  const res = await fetch(PORTAL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ returnUrl: window.location.origin }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data.url;
}
